import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { queryKeys } from '@core/api/queryKeys';

/**
 * Subscribes to the `subscription.changed` realtime push and invalidates `['me']` so a Stripe
 * webhook processed elsewhere (a checkout completed in another tab, a portal card update, a
 * cancellation, etc.) unlocks/relocks the paywall live, without a manual reload.
 *
 * Lives here — called unconditionally from `SubscriptionGuard` — rather than in
 * `useRealtimeInvalidation`'s general registry (where it used to live). `SubscriptionGuard` mounts
 * for EVERY authenticated+approved user regardless of entitlement, whereas `useRealtimeInvalidation`
 * only runs inside `AppShell`, which `SubscriptionGuard` itself never renders while it is blocking an
 * unentitled user behind `SubscribePage` — so the old registration was unreachable exactly when a
 * lapsed/never-subscribed user needed it most (a checkout completing while THEY were staring at the
 * paywall never invalidated `['me']`, and the guard never re-checked). Centralizing the subscription
 * here — one hook, one call site, always mounted — is the single source of truth for this event; it
 * was deliberately REMOVED from `useRealtimeInvalidation`'s registry so the two never double-fire.
 */
export function useSubscriptionChangedInvalidation(): void {
  const realtime = useRealtime();
  const qc = useQueryClient();

  useEffect(() => {
    return realtime.subscribe('subscription.changed', () => {
      qc.invalidateQueries({ queryKey: queryKeys.me });
    });
  }, [realtime, qc]);
}
